import { useState } from "react";
import { dragVariables, positiveVariables } from "../kernel/decisionKernel.js";
import { corpusManifest, manifestSummary } from "../knowledge/corpusManifest.js";
import { custodySummary } from "../knowledge/corpusCustodyRegistry.js";
import {
  empiricalMetricDefinitions,
  empiricalMetricResults,
  empiricalScenarioFixtures,
  empiricalScenarioSets,
  empiricalValidationSummary
} from "../knowledge/empiricalValidation.js";
import { equationCatalog, equationSummary } from "../knowledge/equationCatalog.js";
import { promotionAssessmentForEquation, promotionSummary } from "../knowledge/equationPromotion.js";
import { provenanceByEquationId, provenanceSummary } from "../knowledge/equationProvenance.js";
import { productWorkflows } from "../knowledge/productWorkflows.js";
import { receiptLedgerSummary } from "../knowledge/receiptLedger.js";
import { useRuntimeProofStatus } from "../knowledge/runtimeProofStatus.js";
import { validationGates } from "../knowledge/validationGates.js";
import { Meter } from "./Meter.jsx";
import { QuoteLedger } from "./QuoteLedger.jsx";
import { LucideIcon } from "./lucideIconMap.js";

function StatusPill({ children, tone = "neutral" }) {
  return <span className={`status-pill ${tone}`}>{children}</span>;
}

const variableLabels = {
  advantage: "Advantage",
  information: "Information",
  timing: "Timing",
  position: "Position",
  energy: "Energy",
  harmony: "Harmony",
  cost: "Cost",
  risk: "Risk",
  uncertainty: "Uncertainty",
  exposure: "Exposure",
  disorder: "Disorder"
};

function proofSealTone(value) {
  if (value === "blocked") return "blocked";
  if (value === "warn") return "warn";
  return "ready";
}

function ProofSealBar({ receipt }) {
  const { durable, deployment, loaded, sourceAvailable } = useRuntimeProofStatus();
  const proofPending = loaded && !sourceAvailable;
  const seals = [
    { label: "Options", value: receipt.ranked.length, tone: "ready" },
    {
      label: "Local Ready",
      value: proofPending ? "Proof pending" : loaded ? (deployment.localReady ? "Yes" : "No") : "Loading",
      tone: proofPending ? "warn" : loaded ? (deployment.localReady ? "ready" : "blocked") : "warn"
    },
    {
      label: "Production",
      value: proofPending ? "Not claimed" : loaded ? (deployment.productionDeployClaim ? "Claimed" : "Blocked") : "Loading",
      tone: proofPending ? "blocked" : loaded ? (deployment.productionDeployClaim ? "ready" : "blocked") : "warn"
    },
    {
      label: "Custody",
      value: proofPending ? "Not proven" : loaded ? (durable.serverCustodyClaim ? "Proven" : "Blocked") : "Loading",
      tone: proofPending ? "blocked" : loaded ? (durable.serverCustodyClaim ? "ready" : "blocked") : "warn"
    },
    { label: "Model", value: "Operator", tone: "warn" }
  ];

  return (
    <section className="proof-seal-bar" aria-label="Proof and readiness status">
      {seals.map((seal) => (
        <div className={`proof-seal ${proofSealTone(seal.tone)}`} key={seal.label}>
          <span>{seal.label}</span>
          <strong>{seal.value}</strong>
        </div>
      ))}
    </section>
  );
}

function DecisionPathLedger({ currentDomain, options, selectedOption, setSelectedOptionId }) {
  return (
    <section className="decision-path-ledger" aria-label="Decision path ledger">
      <div className="ledger-head">
        <span>Decision Path</span>
        <span>Score</span>
        <span>Gates</span>
        <span>Missing</span>
        <span>Next Action</span>
      </div>
      {options.map((option, index) => {
        const sourceOption = currentDomain.options.find((entry) => entry.id === option.id);
        const active = option.id === selectedOption.id;
        const blocked = option.gate.value === 0;
        const gates = sourceOption?.gates ?? [];
        const passedGates = gates.filter((gate) => gate.required && gate.passed).length;
        const requiredGates = gates.filter((gate) => gate.required).length;
        return (
          <button
            className={`decision-stone ${active ? "active" : ""} ${blocked ? "blocked" : ""}`}
            key={option.id}
            onClick={() => setSelectedOptionId(option.id)}
            aria-pressed={active}
          >
            <span className="stone-decision">
              <span className="stone-mark" aria-hidden="true">{String.fromCharCode(65 + index)}</span>
              <span className="stone-title">
                <strong>{sourceOption?.title ?? option.label}</strong>
                <small>{option.label}</small>
                <em>{active ? "Selected path" : blocked ? "Blocked path" : "Available path"}</em>
              </span>
            </span>
            <span className="stone-score">
              <strong>{option.scorePercent}</strong>
              <small>/100</small>
            </span>
            <span className={blocked ? "stone-gate danger" : "stone-gate good"}>
              {passedGates}/{requiredGates} passed
            </span>
            <span className="stone-missing">{option.missingEvidence.length}</span>
            <span className="stone-action">
              {option.nextAction}
              <LucideIcon name="ArrowRight" size={16} />
            </span>
          </button>
        );
      })}
    </section>
  );
}

function EquationGarden({ option, selectedOption, onChange }) {
  const renderVariable = (key, tone) => (
    <label className={`garden-row ${tone}`} key={key}>
      <span>{variableLabels[key]}</span>
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={option.variables[key]}
        onChange={(event) => onChange(key, Number(event.target.value))}
      />
      <strong>{Math.round(option.variables[key] * 100)}</strong>
    </label>
  );

  return (
    <section className="equation-garden" aria-label="Equation garden">
      <div className="garden-header">
        <div>
          <span>Equation Garden</span>
          <h3>{option.title}</h3>
        </div>
        <div className="garden-score">
          <span>Net Score</span>
          <strong>{selectedOption.scorePercent}</strong>
        </div>
      </div>
      <div className="garden-grid">
        <div>
          <h4>Positive Drivers</h4>
          {positiveVariables.map((key) => renderVariable(key, "positive"))}
        </div>
        <div className="garden-ensō" aria-hidden="true">
          <span />
        </div>
        <div>
          <h4>Drag Factors</h4>
          {dragVariables.map((key) => renderVariable(key, "drag"))}
        </div>
      </div>
      <div className="garden-summary">
        <div>
          <span>Force</span>
          <strong>{Math.round(selectedOption.positiveForce * 100)}%</strong>
        </div>
        <div>
          <span>Drag</span>
          <strong>{Math.round(selectedOption.drag * 100)}%</strong>
        </div>
        <div>
          <span>Gate status</span>
          <strong>{selectedOption.gate.failed.length ? "Blocked" : "Passed"}</strong>
        </div>
        <div>
          <span>Missing evidence</span>
          <strong>{selectedOption.missingEvidence.length}</strong>
        </div>
      </div>
    </section>
  );
}

export function DecisionWorkspace({ currentDomain, receipt, selectedOption, editableOption, setSelectedOptionId, updateVariable }) {
  const options = receipt.ranked;

  return (
    <>
      <section className="decision-hero" role="tabpanel" aria-label="Decision workspace">
        <div className="hero-copy">
          <span>Evidence-Gated Decision</span>
          <h2>{selectedOption.nextAction}</h2>
          <p>{currentDomain.objective}</p>
        </div>
        <div className="hero-boundary">
          <StatusPill tone={selectedOption.gate.value === 0 ? "danger" : "good"}>
            {selectedOption.gate.value === 0 ? "Gate blocked" : "Required gates passed"}
          </StatusPill>
          <StatusPill>{selectedOption.missingEvidence.length} missing evidence</StatusPill>
          <StatusPill tone="warn">Operator model, not proof</StatusPill>
        </div>
      </section>

      <ProofSealBar receipt={receipt} />

      <div className="decision-workbench">
        <DecisionPathLedger
          currentDomain={currentDomain}
          options={options}
          selectedOption={selectedOption}
          setSelectedOptionId={setSelectedOptionId}
        />
        <EquationGarden option={editableOption} selectedOption={selectedOption} onChange={updateVariable} />
      </div>
    </>
  );
}

export function EvidenceWorkspace({ selectedOption, editableOption }) {
  const gates = editableOption?.gates ?? selectedOption.gate?.failed ?? [];

  return (
    <section className="view-panel evidence-workspace" role="tabpanel" aria-label="Evidence workspace">
      <div className="view-header">
        <div>
          <span>Evidence review</span>
          <h2>{selectedOption.label} · {selectedOption.scorePercent}</h2>
        </div>
        <StatusPill tone={selectedOption.gate.value === 0 ? "danger" : "good"}>
          {selectedOption.gate.value === 0 ? "Blocked" : "Verified"}
        </StatusPill>
      </div>
      <div className="evidence-columns">
        <article>
          <h3>Available Evidence</h3>
          {selectedOption.evidence.length ? (
            selectedOption.evidence.map((item) => <p className="evidence-line" key={item}>{item}</p>)
          ) : (
            <p className="empty-state">No verified evidence attached to this option.</p>
          )}
        </article>
        <article>
          <h3>Missing Evidence</h3>
          {selectedOption.missingEvidence.length ? (
            selectedOption.missingEvidence.map((item) => <p className="evidence-line missing" key={item}>{item}</p>)
          ) : (
            <p className="empty-state">No missing evidence for this option.</p>
          )}
        </article>
        <article>
          <h3>Gate Status</h3>
          {gates.map((gate) => (
            <p className={`evidence-line ${gate.passed ? "passed" : "missing"}`} key={gate.label}>
              {gate.passed ? "Passed" : "Blocked"} · {gate.label}
            </p>
          ))}
          {gates.length === 0 ? <p className="empty-state">No raw gate records available for this option.</p> : null}
        </article>
      </div>
      <QuoteLedger />
    </section>
  );
}

export function SourceVaultWorkspace() {
  const [sourceFilter, setSourceFilter] = useState("");
  const summary = manifestSummary();
  const custody = custodySummary();
  const filteredSources = corpusManifest.filter((source) => {
    const search = sourceFilter.trim().toLowerCase();
    if (!search) return true;
    return [source.id, source.title, source.lane, source.tradition].filter(Boolean).some((value) => value.toLowerCase().includes(search));
  });

  return (
    <section className="view-panel" role="tabpanel" aria-label="Source vault">
      <div className="view-header">
        <div>
          <span>Corpus custody</span>
          <h2>Source Vault</h2>
        </div>
        <div className="summary-strip">
          <StatusPill>{summary.total} sources</StatusPill>
          <StatusPill tone="good">{summary.countable} countable</StatusPill>
          <StatusPill>{custody.total} custody records</StatusPill>
        </div>
      </div>
      <label className="source-filter">
        <span>Search source vault</span>
        <input value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} placeholder="Sunzi, Bushido, anchor, licensed..." />
      </label>
      <div className="source-table">
        {filteredSources.map((source) => (
          <article className="source-table-row" key={source.id}>
            <div>
              <strong>{source.title}</strong>
              <span>{source.id} · {source.tradition}</span>
            </div>
            <StatusPill tone={source.lane === "PUBLIC_DOMAIN_COUNTABLE" ? "good" : "warn"}>{source.lane}</StatusPill>
          </article>
        ))}
        {filteredSources.length === 0 ? <p className="empty-state">No source records match this filter.</p> : null}
      </div>
      <QuoteLedger />
    </section>
  );
}

export function EquationWorkspace() {
  const equationHealth = equationSummary();
  const provenanceHealth = provenanceSummary();
  const promotionHealth = promotionSummary();

  return (
    <section className="view-panel" role="tabpanel" aria-label="Equation workbench">
      <div className="view-header">
        <div>
          <span>Operator governance</span>
          <h2>Equation Workbench</h2>
        </div>
        <div className="summary-strip">
          <StatusPill>{equationCatalog.length} operators</StatusPill>
          <StatusPill>{provenanceHealth.spotChecked} spot checked</StatusPill>
          <StatusPill tone="warn">{promotionHealth.blockedGates} blockers</StatusPill>
        </div>
      </div>
      <div className="equation-list">
        {equationCatalog.map((equation) => {
          const provenance = provenanceByEquationId(equation.id);
          const promotion = promotionAssessmentForEquation(equation.id);
          return (
            <article className="equation-row" key={equation.id}>
              <div>
                <strong>{equation.label}</strong>
                <span>v{equation.version} · {equation.introducedAt} · {provenance?.evidenceClass ?? "NO_PROVENANCE"}</span>
              </div>
              <StatusPill tone={equation.lifecycleState === "RESTRICTED" ? "danger" : "warn"}>{equation.lifecycleState}</StatusPill>
              <StatusPill>{promotion?.decision ?? "NO_PROMOTION"}</StatusPill>
            </article>
          );
        })}
      </div>
      <p className="view-boundary">Lifecycle states: {Object.keys(equationHealth.lifecycleStates).join(", ")}. No equation claims formal proof or predictive accuracy.</p>
    </section>
  );
}

export function ScenarioWorkspace() {
  const summary = empiricalValidationSummary();
  const metrics = empiricalMetricResults();

  return (
    <section className="view-panel" role="tabpanel" aria-label="Scenario validation">
      <div className="view-header">
        <div>
          <span>Validation fixtures</span>
          <h2>Scenarios</h2>
        </div>
        <div className="summary-strip">
          <StatusPill>{summary.scenarioSets} sets</StatusPill>
          <StatusPill>{summary.scenarioCount} fixtures</StatusPill>
          <StatusPill tone="warn">Predictive claims blocked</StatusPill>
        </div>
      </div>
      <div className="metric-grid">
        {empiricalMetricDefinitions.map((metric) => (
          <article className="metric-card" key={metric.id}>
            <span>{metric.label}</span>
            <strong>{metrics[metric.id] ?? "pending"}</strong>
            <p>{metric.claimBoundary}</p>
          </article>
        ))}
      </div>
      <div className="scenario-grid">
        {empiricalScenarioSets.map((set) => (
          <article className="scenario-card" key={set.id}>
            <h3>{set.label}</h3>
            <span>{set.status} · {set.scenarioCount} scenario</span>
            <p>{set.blockedClaims.join(", ")}</p>
          </article>
        ))}
      </div>
      <div className="source-table compact-table">
        {empiricalScenarioFixtures.map((fixture) => (
          <article className="source-table-row" key={fixture.id}>
            <div>
              <strong>{fixture.id}</strong>
              <span>{fixture.reviewerNotes}</span>
            </div>
            <Meter value={Math.round(fixture.score * 100)} label="Score" tone={fixture.expectedGateFailure ? "red" : "teal"} />
          </article>
        ))}
      </div>
    </section>
  );
}

export function ReceiptsWorkspace({ localLedgerEntries, localLedgerSummary }) {
  const ledger = receiptLedgerSummary();

  return (
    <section className="view-panel" role="tabpanel" aria-label="Receipt ledger">
      <div className="view-header">
        <div>
          <span>Custody and exports</span>
          <h2>Receipt Ledger</h2>
        </div>
        <div className="summary-strip">
          <StatusPill>{ledger.total} generated</StatusPill>
          <StatusPill>{localLedgerSummary.total} local decisions</StatusPill>
          <StatusPill tone={localLedgerSummary.blocked ? "danger" : "good"}>{localLedgerSummary.blocked} blocked</StatusPill>
        </div>
      </div>
      <div className="receipt-grid">
        {Object.entries(ledger)
          .filter(([, value]) => typeof value === "number")
          .map(([key, value]) => (
            <article className="metric-card" key={key}>
              <span>{key.replaceAll("_", " ")}</span>
              <strong>{value}</strong>
              <p>Generated artifact count from the local receipt ledger.</p>
            </article>
          ))}
      </div>
      <div className="source-table">
        {localLedgerEntries.map((entry) => (
          <article className="source-table-row" key={entry.id}>
            <div>
              <strong>{entry.domainLabel} · {entry.selectedOptionLabel}</strong>
              <span>{entry.createdAt} · {entry.claimBoundary}</span>
            </div>
            <StatusPill>{entry.scorePercent ?? 0}</StatusPill>
          </article>
        ))}
        {localLedgerEntries.length === 0 ? <p className="empty-state">No local decision receipts saved yet.</p> : null}
      </div>
    </section>
  );
}

export function ReadinessWorkspace() {
  const { durable, deployment } = useRuntimeProofStatus();

  return (
    <section className="view-panel" role="tabpanel" aria-label="Release readiness">
      <div className="view-header">
        <div>
          <span>Release proof</span>
          <h2>Readiness</h2>
        </div>
        <div className="summary-strip">
          <StatusPill tone={deployment.localReady ? "good" : "danger"}>Local {deployment.localReady ? "ready" : "blocked"}</StatusPill>
          <StatusPill tone={deployment.liveUrlVerified ? "good" : "warn"}>Live {deployment.liveUrlVerified ? "verified" : "blocked"}</StatusPill>
          <StatusPill tone={deployment.productionDeployClaim ? "good" : "danger"}>Production {deployment.productionDeployClaim ? "claimed" : "not claimed"}</StatusPill>
        </div>
      </div>
      <div className="readiness-grid">
        <article>
          <h3>Deployment</h3>
          <p>{deployment.panelBoundary}</p>
          <p>{deployment.deployMode ?? "production deploy mode missing"}</p>
          <strong>{deployment.manualBlockers} manual blockers</strong>
        </article>
        <article>
          <h3>Durable Custody</h3>
          <p>{durable.claimBoundary}</p>
          <p>D1/R2 server custody claim: {durable.serverCustodyClaim ? "smoke-proven" : "blocked"}</p>
          <strong>{durable.blockedGates} blocked gates</strong>
        </article>
        <article>
          <h3>Validation Gates</h3>
          {validationGates.slice(0, 6).map((gate) => (
            <p key={gate.id}>{gate.label} · {gate.status}</p>
          ))}
        </article>
        <article>
          <h3>Product Workflows</h3>
          {productWorkflows.map((workflow) => (
            <p key={workflow.id}>{workflow.label} · {workflow.requiredOutputs.length} outputs</p>
          ))}
        </article>
      </div>
    </section>
  );
}
